import { format } from 'date-fns'
import { useRouter } from 'next/router'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { INTENTIONAL_RELOAD_KEY_NAME } from '@/constant/segment'
import { DefaultPropertiesContext } from '@/context/DefaultPropertiesContext'
import { formatSearchParams, searchCategoriesByRegion, searchLocation } from '@/helpers/SearchHelper'
import { getUrlPrefix, toName } from '@/helpers/UrlHelper'
import { logger } from '@/logging/Logger'
import { ModalTypes, TopbarConfigurationDestination, TrackSearchModalInput } from '@/types'
import Icon from './Icons'
import Modal from './Modal'
import {
  ButtonWrapper,
  Calendar,
  CloseSvg,
  Content,
  DateButtonWrapper,
  DateInput,
  DatePickerWrap,
  DatePickerWrapper,
  ExperienceInput,
  IconSvg,
  LocationInput,
  ModalContainer,
  ModalHeader,
  Result,
  ResultsContainer,
  SearchDatePicker,
  SearchSuggestionsWrap,
  SearchWrapper,
  SearchWrapperIcon,
  SearchWrapperInputs,
  Text,
} from './SearchModal.styles'
import { Button, DatePicker, Textfield } from '@big-red-group/storefront-common-ui'
import '@big-red-group/storefront-common-ui/dist/datepicker.css'

const log = logger()

type SearchModalProps = {
  initialActiveInput: string
  isOpen: boolean
  type?: ModalTypes
  placement: string
  onClose: (performedSearch?: boolean) => void
  onSearch: (interactionInput: TrackSearchModalInput) => void
  destinations: TopbarConfigurationDestination[]
  part1Type: string | null
  part2Type: string | null
  part1: string
  part2: string | undefined
  part3: string | undefined
}
// TODO: Component test
function SearchModal({
  initialActiveInput,
  isOpen,
  onClose,
  onSearch,
  type = 'generic',
  destinations,
  part1Type,
  part2Type,
  part1,
  part2,
  part3,
  placement,
}: SearchModalProps) {
  const router = useRouter()
  const defaultProperties = useContext(DefaultPropertiesContext)
  const baseUrl = getUrlPrefix(defaultProperties?.agentConfiguration?.handlerKey)
  // Refs are used here to automatically adjust focus to inputs
  const locationRef = useRef(null)
  const experienceRef = useRef(null)
  const dateRef = useRef(null)

  const isMobile = useMediaQuery({ maxWidth: 767 })

  const dateSeparator = '_'
  // TODO: Use locale if needed
  const localeArgs: Intl.LocalesArgument = undefined
  const dateOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }

  // Input value state
  const [locationValue, setLocationValue] = useState('')
  const [experienceValue, setExperienceValue] = useState('')
  const [dateValue, setDateValue] = useState('')
  const [trackingDateValue, setTrackingDateValue] = useState('')
  const [searchQuery, setSearchQuery] = useState<{ location: string; experience: string; date: string }>({
    location: '',
    experience: '',
    date: '',
  })

  // State to hold the results of search
  const [location, setLocation] = useState<[string, string][]>([])
  const [experience, setExperience] = useState<[string, string][]>([])
  // Used to toggle the modal states based off which input is selected
  const [isLocationFocused, setIsLocationFocused] = useState(false)
  const [isExperienceFocused, setIsExperienceFocused] = useState(false)
  const [isDateFocused, setIsDateFocused] = useState(false)
  const [locationInput, setLocationInput] = useState('')
  const [selectedPopularLocation, toggleSelectedPopularLocation] = useState(false)
  const [experienceInput, setExperienceInput] = useState('')
  const [selectedPopularExperience, toggleSelectedPopularExperience] = useState(false)
  const [isQuickDateSelector, toggleIsQuickDateSelector] = useState(false)
  const [quickDateSelectorValue, setQuickDateSelectorValue] = useState<number | undefined>(undefined)

  // Calendar handlers
  const [startDate, setStartDate] = useState<Date | null>(new Date())
  const [endDate, setEndDate] = useState<Date | null>(new Date())

  const popularLocations = [
    { title: 'Gold Coast', href: 'gold-coast' },
    { title: 'Cairns', href: 'cairns' },
    { title: 'Sydney', href: 'sydney' },
    { title: 'Queenstown', href: 'queenstown' },
    { title: 'Adelaide', href: 'adelaide' },
  ]

  const defaultExperiences = [
    { title: 'Hot Deals', href: 'hot-deals' },
    { title: 'Theme Parks', href: 'theme-parks' },
    { title: 'Zoos & Aquariums', href: 'zoos-and-aquariums' },
    { title: 'Attractions', href: 'attractions' },
    { title: 'Reef & Island Cruises', href: 'reef-and-island-cruises' },
  ]

  const [popularExperiences, setPopularExperiences] = useState(defaultExperiences)

  // Handle initial focus
  const initialFocusRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    switch (initialActiveInput) {
      case 'location':
        initialFocusRef.current = locationRef.current
        break
      case 'experience':
        initialFocusRef.current = experienceRef.current
        break
      case 'date':
        initialFocusRef.current = dateRef.current
        break
      default:
        initialFocusRef.current = locationRef.current
    }
  }, [initialActiveInput, locationRef, experienceRef, dateRef])

  // Handle selected input on open
  useEffect(() => {
    switch (initialActiveInput) {
      case 'location':
        setIsLocationFocused(true)
        setIsExperienceFocused(false)
        setIsDateFocused(false)
        break
      case 'experience':
        setIsExperienceFocused(true)
        setIsLocationFocused(false)
        setIsDateFocused(false)
        break
      case 'date':
        setIsDateFocused(true)
        setIsExperienceFocused(false)
        setIsLocationFocused(false)
        break
    }
  }, [isOpen])

  // Input change handlers
  const handleLocationChange = (value: string) => {
    getLocations(value)
    setLocationValue(value)
    setLocationInput(value)
  }

  const handleExperienceChange = (value: string) => {
    setExperienceValue(value)
    const filteredData: [string, string][] = popularExperiences
      .filter((item) => item.title.toLowerCase().startsWith(value.toLowerCase()))
      .map((item) => [item.title, item.href])

    setExperience(value !== '' ? filteredData : [])
    setExperienceInput(value)
  }

  const handleDateChange = (value: string) => {
    setDateValue(value)
  }

  // Input event handlers (Used to toggle which modal screen is shown (location, experience, date))
  const handleLocationFocus = () => {
    isExperienceFocused ? setIsExperienceFocused(false) : null
    isDateFocused ? setIsDateFocused(false) : null
    setIsLocationFocused(true)
  }

  const handleLocationBlur = (relatedTarget: any) => {
    if (!isLocationFocused || (relatedTarget != null && relatedTarget?.closest('.results-wrapper'))) return null
    setIsLocationFocused(false)
  }

  const handleExperienceFocus = () => {
    isLocationFocused ? setIsLocationFocused(false) : null
    isDateFocused ? setIsDateFocused(false) : null
    setIsExperienceFocused(true)
  }

  const handleExperienceBlur = (relatedTarget: any) => {
    if (!isExperienceFocused || (relatedTarget != null && relatedTarget?.closest('.results-wrapper'))) return null
    setIsExperienceFocused(false)
  }

  const handleDateFocus = () => {
    isExperienceFocused ? setIsExperienceFocused(false) : null
    isLocationFocused ? setIsLocationFocused(false) : null
    setIsDateFocused(true)
    window.setTimeout(() => {
      const searchModal = document.getElementById('search-modal')
      if (searchModal) {
        searchModal.scrollTo({ top: searchModal.scrollHeight, behavior: 'smooth' })
      }
    }, 300)
  }

  const handleDateBlur = (relatedTarget: any) => {
    if (!isDateFocused || (relatedTarget != null && relatedTarget?.closest('.results-wrapper'))) return null
    setIsDateFocused(false)
  }

  // Filter locations from JSON
  const getLocations = (location: string) => {
    setLocation(searchLocation(location).slice(0, 5))
    return searchLocation(location)
  }

  const formatDateValue = (startDate: Date, endDate: Date): [string, string] => {
    const formattedStartDate = startDate?.toLocaleDateString(localeArgs, dateOptions)
    const formattedEndDate = endDate?.toLocaleDateString(localeArgs, dateOptions)

    const formattedTrackingStartDate = format(startDate, 'yyyy-MM-dd')
    const formattedTrackingEndDate = endDate ? format(endDate, 'yyyy-MM-dd') : ''

    const searchLabel =
      formattedStartDate === formattedEndDate
        ? `${formattedStartDate ?? ''}`
        : `${formattedStartDate ?? ''} - ${formattedEndDate ?? ''}`

    return [`${searchLabel}`, `${formattedTrackingStartDate}${dateSeparator}${formattedTrackingEndDate}`]
  }

  const [isSearchValid, setIsSearchValid] = useState(false)

  useEffect(() => {
    setIsSearchValid(locationValue !== '' || (experienceValue !== '' && experienceValue !== 'All'))
  }, [locationValue, experienceValue])

  // Handle search button
  const handleSearch = async () => {
    const [startDateSplit, endDateSplit] = trackingDateValue.split(dateSeparator)

    const part1 = searchQuery.location
    const part2 = searchQuery.experience ? `/${searchQuery.experience}` : ''
    const part3 = dateValue === 'Flexible' || !searchQuery.date ? '' : `/${searchQuery.date}`
    window.sessionStorage.setItem(INTENTIONAL_RELOAD_KEY_NAME, 'true')
    await router.push(`${baseUrl}/en/search/${part1}${part2}${part3}`)
    onClose(true)
  }

  const loadPopularExperiences = useCallback(
    (location: string) => {
      const popularExperiencesForRegion = searchCategoriesByRegion(destinations, location) || []
      location && setPopularExperiences(popularExperiencesForRegion)
      // return here so this can be used to search
      return popularExperiencesForRegion
    },
    [destinations]
  )

  // Handle result click
  const handleLocationResultClick = (location: [string, string]) => {
    setLocationValue(location[0])
    setSearchQuery({ location: location[1], experience: '', date: searchQuery.date })
    setIsLocationFocused(false)
    loadPopularExperiences(location[0])
    setIsSearchValid(true)
    toggleSelectedPopularLocation(false)
    // Clear experience input when a location is selected
    setExperienceValue('All')
    setExperience([])
    setExperienceInput('')
  }

  const handleLocationCategoryClick = (location: { title: string; href: string }) => {
    setLocationValue(location.title)
    loadPopularExperiences(location.title)
    setIsLocationFocused(false)
    setIsSearchValid(true)
    setSearchQuery({ location: location.href, experience: '', date: searchQuery.date })
    toggleSelectedPopularLocation(true)
    // Clear experience input when a location is selected
    setExperienceValue('All')
    setExperience([])
    setExperienceInput('')
  }

  const handleExperienceResultClick = (experience: [string, string]) => {
    setExperienceValue(experience[0])
    setIsExperienceFocused(false)
    setIsSearchValid(true)
    setSearchQuery({ location: searchQuery.location, experience: experience[1], date: searchQuery.date })
    toggleSelectedPopularExperience(false)
  }

  const handleExperienceCategoryClick = (experience: { title: string; href: string }) => {
    // All quick select in experience (defaults the experience to none)
    if (experience.title === 'All') {
      setExperienceValue('All')
      setIsExperienceFocused(false)
      setSearchQuery({ location: searchQuery.location, experience: '', date: searchQuery.date })
      return
    }
    setExperienceValue(experience.title)
    setIsExperienceFocused(false)
    setIsSearchValid(true)
    setSearchQuery({ location: searchQuery.location || '', experience: experience.href, date: searchQuery.date })
    toggleSelectedPopularExperience(true)
  }

  const handleCalendarDateChange = (dates: Date[]) => {
    const [start, end] = dates
    setStartDate(start)
    setEndDate(end)
  }

  useEffect(() => {
    // Search Query
    if (startDate && endDate) {
      const [formattedValue, formattedTrackingValue] = formatDateValue(startDate, endDate)
      setDateValue(formattedValue)
      setTrackingDateValue(formattedTrackingValue)
      const queryStartDate = startDate.toLocaleDateString('en-CA')
      const queryEndDate = endDate.toLocaleDateString('en-CA')
      const dateSearchQuery = `${queryStartDate}_${queryEndDate}`

      setSearchQuery({
        location: searchQuery.location,
        experience: searchQuery.experience,
        date: dateSearchQuery,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate])

  const CustomHeader = ({
    monthDate,
    customHeaderCount,
    decreaseMonth,
    increaseMonth,
  }: {
    monthDate: Date
    customHeaderCount: number
    decreaseMonth: () => void
    increaseMonth: () => void
  }) => {
    const startMonth = new Date(monthDate)
    const endMonth = new Date(monthDate)

    startMonth.setMonth(monthDate.getMonth() - customHeaderCount)
    endMonth.setMonth(monthDate.getMonth() + 1 - customHeaderCount)

    return (
      <div>
        <button
          aria-label="Previous Month"
          className={'react-datepicker__navigation react-datepicker__navigation--previous'}
          style={customHeaderCount === 1 ? { visibility: 'hidden' } : undefined}
          onClick={decreaseMonth}
        >
          <Icon name="arrowCircleLeft" />
        </button>
        <span className="react-datepicker__current-month">
          {monthDate
            .toLocaleString('en-US', {
              month: 'long',
              year: 'numeric',
            })
            .toUpperCase()}
        </span>
        <button
          aria-label="Next Month"
          className={'react-datepicker__navigation react-datepicker__navigation--next'}
          style={customHeaderCount === 0 ? { visibility: 'hidden' } : undefined}
          onClick={increaseMonth}
        >
          <Icon name="arrowCircleRight" />
        </button>
        {isMobile && (
          <button
            aria-label="Previous Month"
            className={'react-datepicker__navigation react-datepicker__navigation--previous'}
            onClick={decreaseMonth}
          >
            <Icon name="arrowCircleLeft" />
          </button>
        )}
        {isMobile && (
          <button
            aria-label="Next Month"
            className={'react-datepicker__navigation react-datepicker__navigation--next'}
            onClick={increaseMonth}
          >
            <Icon name="arrowCircleRight" />
          </button>
        )}
      </div>
    )
  }

  // Set default dates on load
  useEffect(() => {
    const searchedLocationValue = formatSearchParams(part1Type, part2Type, part1, part2, part3).part1
    const searchedExperienceValue = formatSearchParams(part1Type, part2Type, part1, part2, part3).part2
    const searchedDateValue = formatSearchParams(part1Type, part2Type, part1, part2, part3).part3
    // Location
    const locations = getLocations(searchedLocationValue || '')?.flat()
    setLocation([])
    setLocationValue(searchedLocationValue || '')
    // Experience
    const experiences = loadPopularExperiences(toName(searchedLocationValue || ''))?.filter(
      (obj) => obj.title === searchedExperienceValue
    )

    setExperience([])

    setExperienceValue(searchedExperienceValue || 'All')

    const queryStartDate = format(startDate!, 'yyyy-MM-dd')
    const queryEndDate = format(endDate! ? endDate! : startDate!, 'yyyy-MM-dd')
    const dateSearchQuery = `${queryStartDate}_${queryEndDate}`
    setDateValue(searchedDateValue || 'Flexible')
    // Search query
    setSearchQuery({
      location: locations[1],
      experience: (experiences && experiences[0]?.href) || '',
      date: dateSearchQuery || '',
    })
  }, [part1Type, part2Type, part1, part2, part3, loadPopularExperiences])

  // Calendar quick date button selectors
  const handleQuickDateSelector = (days: number) => {
    toggleIsQuickDateSelector(true)
    setQuickDateSelectorValue(days)

    const newStartDate = new Date()
    const newEndDate = new Date()

    newStartDate.setDate(newStartDate.getDate() + 1)
    newEndDate.setDate(newEndDate.getDate() + days)

    setStartDate(newStartDate)
    setEndDate(newEndDate)
    setIsDateFocused(false)
  }

  const handleTodayDateSelector = () => {
    toggleIsQuickDateSelector(true)
    setQuickDateSelectorValue(0)

    setStartDate(new Date())
    setEndDate(new Date())
    setIsDateFocused(false)
  }

  const clearInput = (inputName: string) => {
    switch (inputName) {
      case 'location':
        setLocation([])
        setLocationValue('')
        setSearchQuery({ location: '', experience: searchQuery.experience, date: searchQuery.date })
        setPopularExperiences(defaultExperiences)
        setExperienceValue('All')
        return
      case 'experience':
        setExperience([])
        setExperienceValue('All')
        setSearchQuery({ location: searchQuery.location, experience: '', date: searchQuery.date })
        return
      case 'date':
        setStartDate(null)
        setEndDate(null)
        setDateValue('Flexible')
        setSearchQuery({ location: searchQuery.location, experience: searchQuery.experience, date: '' })
        return
    }
  }

  const activeInputClass = () => {
    if (isLocationFocused) {
      return 'location-active'
    } else if (isExperienceFocused) {
      return 'experience-active'
    } else if (isDateFocused) {
      return 'date-active'
    } else {
      return ''
    }
  }

  // TODO: Future release
  // const handleApply = () => {
  //   log.info('Start Date:', startDate.toLocaleDateString())
  //   log.info('End Date:', endDate.toLocaleDateString())
  // }

  return (
    // TODO: Modal top right positioning
    <Modal
      type={type}
      isOpen={isOpen}
      onClose={onClose}
      dataTestId="search-modal"
      width="960px"
      fullScreenMobile={true}
      intialFocus={initialFocusRef}
    >
      <ModalHeader data-testid="search-modal-header">
        <span>Search</span>
        {/* TODO use common-ui Icon - cannot use now because `onClose` type is: `(performedSearch?: boolean) => void` */}
        <Icon name="close" onClick={onClose} />
      </ModalHeader>
      <ModalContainer className={activeInputClass()} id="search-modal">
        <SearchWrapper>
          <SearchWrapperIcon>
            <Icon name="search" />
          </SearchWrapperIcon>
          <SearchWrapperInputs>
            <LocationInput>
              <IconSvg name="search" width={18} height={18} />
              <Textfield
                className="location-search-input"
                placeholder="E.g. Sydney"
                value={locationValue}
                onFocus={handleLocationFocus}
                onBlur={(e) => handleLocationBlur(e.relatedTarget)}
                onChange={(e: React.FormEvent<HTMLInputElement>) => handleLocationChange(e.currentTarget.value)}
                forwardedRef={locationRef}
                name="search-modal-location"
              />
              <CloseSvg
                name="close"
                width={10}
                height={10}
                onClick={() => {
                  clearInput('location')
                }}
              />
            </LocationInput>
            <ExperienceInput>
              <IconSvg name="search" width={18} height={18} />
              <Textfield
                className="exp-search-input"
                placeholder="E.g. Theme Parks"
                value={experienceValue}
                onFocus={handleExperienceFocus}
                onBlur={(e) => handleExperienceBlur(e.relatedTarget)}
                onChange={(e: React.FormEvent<HTMLInputElement>) => handleExperienceChange(e.currentTarget.value)}
                forwardedRef={experienceRef}
                readonly
                name="search-modal-experience"
              />
              <CloseSvg
                name="close"
                width={10}
                height={10}
                onClick={() => {
                  clearInput('experience')
                }}
              />
            </ExperienceInput>
            <DateInput>
              <IconSvg name="calendar" width={18} height={18} />
              <Textfield
                className="date-search-input"
                placeholder="Date"
                value={dateValue}
                onChange={(e: React.FormEvent<HTMLInputElement>) => handleDateChange(e.currentTarget.value)}
                onFocus={handleDateFocus}
                onBlur={(e) => handleDateBlur(e.relatedTarget)}
                forwardedRef={dateRef}
                readonly
              />
              <CloseSvg
                name="close"
                width={10}
                height={10}
                onClick={() => {
                  clearInput('date')
                }}
              />
            </DateInput>
            {/* Location */}
            {isLocationFocused &&
              (location.length == 0 ? (
                <>
                  {/* TODO: future release? */}
                  {/* <Text>Search by</Text>
            <Content>
              <Pill>Current Location</Pill>
              <Pill>Everywhere</Pill>
            </Content> */}
                  {/* TODO: Hardcoded for now, will need to add functionality for ecommm to update */}
                  <SearchSuggestionsWrap className="results-wrapper" tabIndex={0}>
                    <Text>Choose from popular locations</Text>
                    <Content>
                      {popularLocations.map((location, index) => {
                        return (
                          <Button
                            key={index}
                            dataTestId={`popular-location-${location.title.toLowerCase()}`}
                            size="S"
                            inverse
                            onClick={() => handleLocationCategoryClick(location)}
                          >
                            {location.title}
                          </Button>
                        )
                      })}
                    </Content>
                  </SearchSuggestionsWrap>
                </>
              ) : (
                <ResultsContainer className="results-wrapper" tabIndex={0}>
                  {location.slice(0, 10).map((result, index) => {
                    return (
                      <Result
                        tabIndex={0}
                        data-testid={`location-result-${result[0]}`}
                        onClick={() => handleLocationResultClick(result)}
                        key={index}
                      >
                        <Icon name="location" width={20} height={20} />
                        {result[0]}
                      </Result>
                    )
                  })}
                </ResultsContainer>
              ))}
            {/* Experience */}
            {isExperienceFocused &&
              (experience.length == 0 ? (
                <>
                  {/* TODO: future release? */}
                  {/* <Text>Search by</Text>
            <Content>
              <Pill>Current Location</Pill>
              <Pill>Everywhere</Pill>
            </Content> */}
                  {/* TODO: Hardcoded for now, will need to add functionality for ecommm to update */}
                  <SearchSuggestionsWrap className="results-wrapper" tabIndex={0}>
                    <Text>Choose from popular {locationValue.length ? locationValue : ''} experiences</Text>
                    <Content>
                      <Button
                        size="S"
                        inverse
                        className="all-btn"
                        onClick={() => handleExperienceCategoryClick({ title: 'All', href: '' })}
                      >
                        All
                      </Button>
                      {popularExperiences?.map((experience, index) => {
                        return (
                          <Button
                            key={index}
                            dataTestId={`popular-experience-${experience.title.toLowerCase()}`}
                            size="S"
                            inverse
                            onClick={() => handleExperienceCategoryClick(experience)}
                          >
                            {experience.title}
                          </Button>
                        )
                      })}
                    </Content>
                  </SearchSuggestionsWrap>
                </>
              ) : (
                <ResultsContainer className="results-wrapper" tabIndex={0}>
                  {experience.slice(0, 10).map((result, index) => {
                    return (
                      <Result
                        tabIndex={0}
                        data-testid={`experience-result-${result[0]}`}
                        onClick={() => handleExperienceResultClick(result)}
                        key={index}
                      >
                        <Icon name="starOutline" width={20} height={20} />
                        {result[0]}
                      </Result>
                    )
                  })}
                </ResultsContainer>
              ))}
            {isDateFocused && (
              <SearchDatePicker className="results-wrapper" tabIndex={0}>
                <ButtonWrapper>
                  <Button onClick={handleTodayDateSelector} size="XS" dataTestId="today-btn" inverse>
                    Today
                  </Button>
                  <Button onClick={() => handleQuickDateSelector(7)} size="XS" dataTestId="next-7-days-btn" inverse>
                    Next 7 Days
                  </Button>
                  <Button onClick={() => clearInput('date')} size="XS" dataTestId="tomorrow-btn" inverse>
                    Flexible
                  </Button>
                </ButtonWrapper>
                <DatePickerWrap>
                  <DatePickerWrapper>
                    <DatePicker
                      wrapperClassName="date_picker full-width"
                      formatWeekDay={(nameOfDay: string) => nameOfDay.slice(0, 1)}
                      calendarContainer={Calendar}
                      selected={startDate}
                      // @ts-ignore
                      onChange={handleCalendarDateChange}
                      renderCustomHeader={CustomHeader}
                      minDate={new Date()}
                      startDate={startDate!}
                      endDate={endDate!}
                      selectsRange
                      inline
                      monthsShown={isMobile ? 1 : 2}
                    />
                  </DatePickerWrapper>
                  <DateButtonWrapper>
                    <Button
                      alternate
                      inverse
                      onClick={() => {
                        clearInput('date')
                      }}
                      size="S"
                    >
                      Clear
                    </Button>
                    <Button onClick={() => setIsDateFocused(false)} size="S">
                      Apply
                    </Button>
                  </DateButtonWrapper>
                </DatePickerWrap>
                {/* Future release */}
                {/* <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <Button inverse onClick={() => {}}>
                Clear Dates
              </Button>
              <Button onClick={handleApply}>Apply</Button>
            </div> */}
              </SearchDatePicker>
            )}
          </SearchWrapperInputs>
          <Button
            className="submit-search-button"
            onClick={handleSearch}
            // disabled={isClient() && document.getElementById('search-modal')?.classList.contains('date-active')}
            disabled={!isSearchValid}
            size="M"
            dataTestId="search-btn-icon"
          >
            Search
          </Button>
        </SearchWrapper>
        {/* <Divider />
        <Heading>Suggested Results</Heading>
        <ResultsWrapper>
          <SuggestedResult>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <p>SkyPoint Day Climb</p>
              <p>$145</p>
            </div>
            <div>
              <Icon name="rightarrow" />
            </div>
          </SuggestedResult>
        </ResultsWrapper> */}
      </ModalContainer>
    </Modal>
  )
}

export default SearchModal
